export const QUOTA_FIELD_MAPPING: Record<string, string> = {
	max_tenants: 'Projects',
	max_devices: 'Devices',
	max_dashboards: 'Dashboards',
	max_panels_per_dashboard: 'Panels per Dashboard',
	max_action_types: 'Action Types',
	max_actions: 'Actions per Device',
	max_roles: 'Roles',
	max_users: 'Users',
	max_streams: 'Streams',
	max_session_rules: 'Session Rules',
	max_api_keys: 'API Keys',
	max_metadata_keys: 'Metadata Keys',
	max_parsers: 'DBC Parsers',
	max_exporters: 'Exporters',
	max_subscriber_groups: 'Subscriber Groups',
	max_alert_rules: 'Alert Rules',
	max_session_types: 'Session Types',
	max_alert_notification_rules: 'Alert Notification Rules',
}

export const mapQuotaFieldToDisplayName = (field: string): string | null => {
	return QUOTA_FIELD_MAPPING[field] || null
}

export const QUOTA_TYPES = [
	'Projects',
	'Devices',
	'Dashboards',
	'Panels per Dashboard',
	'Action Types',
	'Actions per Device',
	'Roles',
	'Users',
	'Streams',
	'Session Rules',
	'DBC Parsers',
	'Exporters',
	'Metadata Keys',
	'API Keys',
	'Subscriber Groups',
	'TTL (Data Retention)',
	'Historical Data (Viewable Window)',
] as const

export type QuotaType = (typeof QUOTA_TYPES)[number]

export const PLAN_IDS = {
	FREE: 'free',
	BASIC: 'basic',
	PRO: 'pro',
	ENTERPRISE: 'enterprise',
} as const

export const PLAN_NAMES = {
	FREE: 'Free',
	BASIC: 'Basic',
	PRO: 'Pro',
	ENTERPRISE: 'Enterprise',
} as const

export const PLAN_HIERARCHY = [PLAN_IDS.FREE, PLAN_IDS.BASIC, PLAN_IDS.PRO, PLAN_IDS.ENTERPRISE] as const

// Normalize a plan identifier (name or id) to a lowercase plan ID
// This handles cases where the API returns plan names (e.g., "Basic") instead of IDs (e.g., "basic")
export const normalizePlanId = (planIdentifier: string | null | undefined): string | null => {
	if (!planIdentifier) {
		return null
	}
	const normalized = planIdentifier.toLowerCase()
	if (Object.values(PLAN_IDS).includes(normalized as (typeof PLAN_IDS)[keyof typeof PLAN_IDS])) {
		return normalized
	}
	for (const [key, name] of Object.entries(PLAN_NAMES)) {
		if (name.toLowerCase() === normalized) {
			return PLAN_IDS[key as keyof typeof PLAN_IDS]
		}
	}
	return normalized
}

export const PLAN_FEATURES = {
	[PLAN_IDS.BASIC]: {
		included: [
			'Full Device Management',
			'Remote Shell (limited)',
			'OTA & Custom Remote Commands',
			'Dashboards',
			'DBC Parsers',
			'RBAC (Admins, Members)',
		],
		excluded: [
			'Pull-Based Actions',
			'Pushers',
			'Sessions (or quota-limited)',
			'Root Project',
			'Dev Mode',
			'Branding',
			'Delayed Support',
		],
	},
	[PLAN_IDS.PRO]: {
		included: [
			'Everything in Basic',
			'Full Sessions & Notifications',
			'Higher Device Quotas',
			'Expanded OTA & Action quotas',
			'Payment Insights',
			'Pushers (self-serve integrations)',
			'Quick-response Support',
		],
		excluded: ['Pull-Based Actions', 'Root Project', 'Unlimited Projects (cap at 3)', 'Branding', 'Dev Mode'],
	},
	[PLAN_IDS.ENTERPRISE]: {
		included: [
			'Everything in Pro',
			'Branding & Theming',
			'Unlimited Projects',
			'Pull-Based Actions',
			'Root Project (superadmin)',
			'Dev Mode (infra control, ephemeral ClickHouse)',
			'Custom domain (client-name.bytebeam.io)',
			'Dedicated infra, security, compliance',
			'Automatic migration (devices + data)',
			'Priority support & SLAs',
		],
		excluded: [],
	},
}

export const PLAN_BADGES = {
	[PLAN_IDS.BASIC]: null,
	[PLAN_IDS.PRO]: 'Most Popular',
	[PLAN_IDS.ENTERPRISE]: null,
} as const

export const PLAN_POPULAR = {
	[PLAN_IDS.BASIC]: false,
	[PLAN_IDS.PRO]: true,
	[PLAN_IDS.ENTERPRISE]: false,
} as const

export const canUpgrade = (fromPlanId: string | null | undefined, toPlanId: string | null | undefined): boolean => {
	const normalizedFrom = normalizePlanId(fromPlanId)
	const normalizedTo = normalizePlanId(toPlanId)

	// If either plan is invalid, we can't determine upgrade eligibility
	if (!normalizedFrom || !normalizedTo) {
		return false
	}

	const fromIndex = PLAN_HIERARCHY.indexOf(normalizedFrom as (typeof PLAN_HIERARCHY)[number])
	const toIndex = PLAN_HIERARCHY.indexOf(normalizedTo as (typeof PLAN_HIERARCHY)[number])

	// If either plan is not in the hierarchy, we can't determine upgrade eligibility
	if (fromIndex === -1 || toIndex === -1) {
		return false
	}

	return toIndex > fromIndex
}

export const calculateYearlyPrice = (monthlyPrice: number): number => {
	return Math.round(monthlyPrice * 12)
}

export const formatPrice = (price: number | null, currency: 'USD' | 'INR' = 'USD'): string => {
	if (price === null) {
		return 'Free'
	}
	const symbol = currency === 'USD' ? '$' : '₹'
	return `${symbol}${price.toLocaleString()}`
}

export const ORGANIZATION_CREATION_LIMIT_PER_USER = 5
